/**
 * Deliverables summary — PURE rollup logic for the first-class
 * `deliverables` collection (Ask #1). No storage, no clock reads
 * unless the caller omits `nowMs`; views stay thin by leaning on this.
 */

import type { Deliverable } from '../db/types';

export type DeliverableStatus = Deliverable['status'];

export interface DeliverablesSummary {
  /** Total deliverables considered. */
  total: number;
  /** Count per status — every status key always present (zeros included). */
  byStatus: Record<DeliverableStatus, number>;
  /** Due date in the past and not yet delivered/accepted — still owed. */
  overdue: Deliverable[];
}

/** Statuses that mean the obligation is discharged — never overdue. */
const DONE_STATUSES: ReadonlySet<DeliverableStatus> = new Set([
  'delivered',
  'accepted',
]);

/**
 * True when a deliverable's committed date has passed and the work has
 * not been delivered/accepted. A deliverable with no `dueDate` is never
 * overdue — no commitment, no breach.
 */
export function isDeliverableOverdue(d: Deliverable, nowMs: number): boolean {
  if (!d.dueDate || DONE_STATUSES.has(d.status)) return false;
  const due = Date.parse(d.dueDate);
  return Number.isFinite(due) && due < nowMs;
}

/** Roll a set of deliverables up into status buckets + overdue list. */
export function summarizeDeliverables(
  items: Deliverable[],
  nowMs: number = Date.now(),
): DeliverablesSummary {
  const byStatus: Record<DeliverableStatus, number> = {
    owed: 0,
    'in-progress': 0,
    delivered: 0,
    accepted: 0,
  };
  const overdue: Deliverable[] = [];
  for (const d of items) {
    byStatus[d.status]++;
    if (isDeliverableOverdue(d, nowMs)) overdue.push(d);
  }
  return { total: items.length, byStatus, overdue };
}
